import { API_BASE_URL } from "src/config/api";
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Paper,
  TextField,
  Card,
  CardContent,
  Button,
  TableContainer,
  CircularProgress,
  Box,
  InputLabel,
  MenuItem,
  Select,
  FormControl,
  Grid,
} from '@mui/material';
import './Wallet.css';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { UserListHead } from '../user';
import Scrollbar from '../../../components/scrollbar/Scrollbar';

const StyledCard = styled('div')(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'light'
      ? theme.palette.grey[100]          
      : theme.palette.background.paper, 

  padding: theme.spacing(2.5),
  borderRadius: 20,

  boxShadow:
    theme.palette.mode === 'light'
      ? '0px 0px 10px rgba(0, 0, 0, 0.15)'
      : '0px 0px 10px rgba(0, 0, 0, 0.6)',
}));

const StyledTableCell = styled(TableCell)`
  padding: 16px;
  text-align: center;
`;

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor:
      theme.palette.mode === 'light'
        ? theme.palette.grey[100]
        : theme.palette.action.hover,
  },
}));

const TABLE_HEAD = [
  { id: 'createdAt', label: <b>Created At</b>, alignRight: false },
  { id: 'balance', label: <b>Balance ($)</b>, alignRight: false },
  { id: 'transactionType', label: <b>Transaction Type</b>, alignRight: false },
  { id: 'transactionReason', label: <b>Transaction Reason</b>, alignRight: false },
];

const Wallet = (props) => {
  const userId = props.admin ? props.userId : localStorage.getItem('userId');
  const token = localStorage.getItem('token');
  const [data, setData] = useState([]);
  const [totalBalance, setTotalBalance] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState('desc');
  const [filterDate, setFilterDate] = useState({ startDate: '', endDate: '' });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const filterUser = '';

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const cleanToken = token.replace(/"/g, '');
        const config = {
          headers: {
            Authorization: `Bearer ${cleanToken}`,
          },
        };
        const response = await axios.get(
          `${API_BASE_URL}/getHistoryOfWallet?userId=${userId}`,
          config
        );
        setData(response.data.data);
      } catch (error) {
        console.error('Error fetching wallet history data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchTotalBalance = async () => {
      setIsLoading(true);
      try {
        const cleanToken = token.replace(/"/g, '');
        const config = {
          headers: {
            Authorization: `Bearer ${cleanToken}`,
          },
        };
        const response = await axios.get(`${API_BASE_URL}/getWalletBalance?userId=${userId}`, config);
        setTotalBalance(response.data);
      } catch (error) {
        console.error('Error fetching total balance data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    fetchTotalBalance();
  }, [userId, token, props.refresh]);

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toISOString().split('T')[0];
  };

  const getTextColorClass = (transactionType) => {
    switch (transactionType) {
      case 'credit':
        return 'green-text';
      case 'debit':
        return 'red-text';
      default:
        return '';
    }
  };
  const handleAddBalanceClick = () => {
    navigate('/dashboard/wallet/payment');
    props.openForm();
  };

  const timeIntervals = [
    { label: 'All day', value: 0 },
    { label: 'Last 1 day', value: 1 },
    { label: 'Last 7 days', value: 7 },
    { label: 'Last 15 days', value: 15 },
    { label: 'Last 30 days', value: 30 },
  ];
  const [selectedInterval, setSelectedInterval] = useState(0);

  const handleSelectChange = (event) => {
    const selectedValue = parseInt(event.target.value, 10);
    setSelectedInterval(selectedValue);
    setFilterDate(getFormattedDate(selectedValue));
  };

  const getFormattedDate = (interval) => {
    if (interval === 0) return { startDate: '', endDate: '' };
    const currentDate = new Date();
    const endDate = currentDate.toISOString().split('T')[0];
    const startDate = new Date(currentDate);
    startDate.setDate(startDate.getDate() - interval + 1);
    const startDateFormatted = startDate.toISOString().split('T')[0];
    return { startDate: startDateFormatted, endDate };
  };

  const filteredData = data.filter((item) => {
    const createdAtDate = formatDate(item.createdAt);
    const { startDate, endDate } = filterDate;
    const dateMatch = startDate && endDate ? createdAtDate >= startDate && createdAtDate <= endDate : true;
    const userMatch = filterUser ? item.userId === filterUser : true;
    return dateMatch && userMatch;
  });

  const sortedData = filteredData.slice().sort((a, b) => {
    const aValue = String(a[orderBy]);
    const bValue = String(b[orderBy]);

    if (order === 'asc') {
      return aValue.localeCompare(bValue);
    }

    return bValue.localeCompare(aValue);
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };
  const capitalizeFirst = (str) => {
    if (!str || typeof str !== 'string') return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  };
  const handleRequestSort = (event, property) => {
    if (property === 'actions' || property === 'serial') return;
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  return (
    <StyledCard>
      <Card>
        <CardContent style={{ textAlign: 'left' }}>
          <Paper className="transaction-history">
            <Typography variant="h6" style={{ paddingBottom: '10px' }}>
              Transaction History
            </Typography>
            <Grid container spacing={2}>
              {/* From Date */}
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  id="startDate"
                  label="From"
                  type="date"
                  value={filterDate.startDate}
                  onChange={(e) => setFilterDate({ ...filterDate, startDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>

              {/* To Date */}
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  id="endDate"
                  label="To"
                  type="date"
                  value={filterDate.endDate}
                  onChange={(e) => setFilterDate({ ...filterDate, endDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  inputProps={{
                    min: filterDate.startDate,
                  }}
                />
              </Grid>

              {/* Date Interval */}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="time-interval-label">Date Interval</InputLabel>
                  <Select
                    labelId="time-interval-label"
                    id="time-interval"
                    value={selectedInterval}
                    onChange={handleSelectChange}
                    label="Time Interval"
                    fullWidth
                  >
                    {timeIntervals.map((interval) => (
                      <MenuItem key={interval.value} value={interval.value}>
                        {interval.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Wallet Balance */}
              {!props.admin && (
                <Grid item xs={12} sm={6} md={6}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="h6"
                      className="total-balance"
                      sx={{
                        marginRight: '10px',
                        fontSize: {
                          xs: '1rem', // smaller font on extra-small screens
                          sm: '1.2rem', // slightly bigger on small screens
                          md: '1.4em', // default for medium and above
                        },
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      Wallet Balance:
                      {isLoading ? (
                        <CircularProgress size={20} sx={{ ml: 1 }} />
                      ) : (
                        <span
                          className={`total-balance-text ${getTextColorClass(totalBalance.transactionType)}`}
                          style={{
                            fontWeight: 'bold',
                            fontSize: window.innerWidth < 600 ? '1rem' : '1.4rem', // responsive balance value
                            marginLeft: '5px',
                          }}
                        >
                          ${totalBalance.balance}
                        </span>
                      )}
                    </Typography>
                    <Button variant="contained" color="primary" onClick={handleAddBalanceClick}>
                      Add Balance
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>

            <Scrollbar>
              <TableContainer sx={{ minWidth: 1000, overflowX: 'auto', pb: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '1rem' }}>
                  {isLoading ? (
                    <CircularProgress />
                  ) : (
                    <>
                      <Table>
                        <UserListHead
                          order={order}
                          orderBy={orderBy}
                          headLabel={TABLE_HEAD}
                          rowCount={data.length}
                          onRequestSort={handleRequestSort}
                        />
                        {sortedData.length === 0 ? (
                          <TableBody>
                            <TableRow>
                              <TableCell align="center" colSpan={5}>
                                <Typography variant="body2" color="text.secondary" sx={{ justifyContent: 'center', display: 'flex' }}>
                                  No Data Found.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        ) : (
                          <TableBody>
                            {sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => {
                              const { _id, createdAt, balance, transactionType, transactionReason } = row;

                              return (
                                <StyledTableRow hover key={_id} tabIndex={-1}>
                                  <TableCell padding="normal">{page * rowsPerPage + index + 1}</TableCell>
                                  <StyledTableCell>{formatDate(createdAt)}</StyledTableCell>
                                  <StyledTableCell>{capitalizeFirst(balance)}</StyledTableCell>
                                  <StyledTableCell>
                                    <span
                                      className={`balance-cell ${getTextColorClass(transactionType)}`}
                                      style={{ fontWeight: 'bold' }}
                                    >{capitalizeFirst(transactionType)}
                                    </span>
                                  </StyledTableCell>
                                  <StyledTableCell>{capitalizeFirst(transactionReason)}</StyledTableCell>
                                </StyledTableRow>
                              );
                            })}
                          </TableBody>
                        )}

                      </Table>
                    </>
                  )}
                </div>
              </TableContainer>
            </Scrollbar>

            <TablePagination
              sx={{ pt: 0, pb: 0 }}
              component="div"
              count={filteredData.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 100]}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </CardContent>
      </Card>
    </StyledCard >
  );
};

Wallet.propTypes = {
  openForm: PropTypes.func,
  refresh: PropTypes.bool,
  admin: PropTypes.bool,
  userId: PropTypes.func,
};

export default Wallet;
